import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { adminAPI } from '../../lib/api';

const META = {
  pass: { Icon: CheckCircle2, color: '#047857', bg: '#ECFDF5', label: 'Pass' },
  warn: { Icon: AlertTriangle, color: '#B45309', bg: '#FFFBEB', label: 'Warning' },
  fail: { Icon: XCircle, color: '#B91C1C', bg: '#FEF2F2', label: 'Fail' },
};
const OVERALL = {
  pass: 'All protections are active',
  warn: 'Protected, with warnings to review',
  fail: 'Action needed: a protection is off',
};

const CheckRow = ({ c }) => {
  const { Icon, color, bg, label } = META[c.status];
  return (
    <div className="sec-row" data-testid={`security-check-${c.id}`} data-status={c.status}>
      <Icon size={20} color={color} style={{ marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1F2937' }}>{c.label}</div>
        <div style={{ fontSize: '0.8125rem', color: '#6B7280', marginTop: 2, overflowWrap: 'anywhere' }}>{c.detail}</div>
        {c.fix && <div data-testid={`security-fix-${c.id}`} style={{ fontSize: '0.8125rem', color, marginTop: 4 }}><b>Fix:</b> {c.fix}</div>}
      </div>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color, background: bg, borderRadius: 999, padding: '0.2rem 0.6rem', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
};

const Summary = ({ res }) => {
  const { Icon, color, bg } = META[res.overall];
  const n = (s) => res.checks.filter((c) => c.status === s).length;
  return (
    <div data-testid="security-check-overall" data-status={res.overall} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', background: bg, borderRadius: 12, padding: '0.9rem 1rem', margin: '1rem 0 0.5rem' }}>
      <Icon size={24} color={color} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color }}>{OVERALL[res.overall]}</div>
        <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
          {n('pass')} passed · {n('warn')} warning{n('warn') === 1 ? '' : 's'} · {n('fail')} failed · last run {new Date(res.ran_at).toLocaleString()}{res.by ? ` by ${res.by}` : ''}
        </div>
      </div>
    </div>
  );
};

export function SecurityCheckTab() {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    adminAPI.latestSecurityCheck().then(({ data }) => setRes(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await adminAPI.runSecurityCheck();
      setRes(data);
      if (data.overall === 'pass') toast.success(OVERALL.pass);
      else if (data.overall === 'warn') toast.warning(OVERALL.warn);
      else toast.error(OVERALL.fail);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The security check could not run');
    } finally {
      setRunning(false);
    }
  };
  const groups = res ? [...new Set(res.checks.map((c) => c.group))] : [];
  return (
    <Card data-testid="security-check-tab" style={{ padding: '1.5rem', border: '1px solid #ECE7DE', borderRadius: 14 }}>
      <style>{'.sec-row{display:grid;grid-template-columns:28px minmax(0,1fr) auto;gap:.75rem;padding:.85rem 0;border-top:1px solid #F1EDE6;align-items:start}'}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', minWidth: 0 }}>
          <ShieldCheck size={26} color="#0076A3" />
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1F2937' }}>Security check</div>
            <div style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Run it after every deploy. Each check runs live against this server.</div>
          </div>
        </div>
        <Button onClick={run} disabled={running} data-testid="security-check-run-btn" style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
          {running ? <><Loader2 size={16} className="mr-2 animate-spin" /> Checking…</> : 'Run check'}
        </Button>
      </div>
      {loading ? <div style={{ marginTop: '1rem', color: '#6B7280' }}>Loading last result…</div> : !res ? (
        <div data-testid="security-check-empty" style={{ marginTop: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>No check has been run yet. Click <b>Run check</b>.</div>
      ) : (
        <>
          <Summary res={res} />
          {groups.map((g) => (
            <div key={g} style={{ marginTop: '0.75rem' }} data-testid={`security-group-${g.toLowerCase().replace(/\s+/g, '-')}`}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9CA3AF' }}>{g}</div>
              {res.checks.filter((c) => c.group === g).map((c) => <CheckRow key={c.id} c={c} />)}
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

export default SecurityCheckTab;
